import { useState } from 'react';
import { useSharing } from '../sync/SharingProvider.js';
import type { AdoptChoice } from '../sync/adopt.js';

/**
 * The first-run question.
 *
 * Rendered at app level, never inside a screen, and it has no dismiss. Both
 * answers are destructive to one side, so the only safe way out is to choose.
 */
export function AdoptDialog(): React.JSX.Element | null {
  const { question, answer } = useSharing();
  const [busy, setBusy] = useState(false);
  if (!question) return null;

  const choose = (choice: AdoptChoice): void => {
    setBusy(true);
    void answer(choice).finally(() => setBusy(false));
  };

  return (
    <div className="adopt-backdrop" role="dialog" aria-modal="true" aria-labelledby="adopt-title">
      <div className="adopt">
        <h2 id="adopt-title">Two plans for this week</h2>
        <p>
          This phone has <strong>{question.localSummary}</strong>. The shared account has{' '}
          <strong>{question.remoteSummary}</strong>. Which should win?
        </p>
        <div className="adopt-actions">
          <button type="button" disabled={busy} onClick={() => choose('local')}>
            Keep this phone&rsquo;s
          </button>
          <button type="button" disabled={busy} onClick={() => choose('remote')}>
            Use the shared one
          </button>
          <button type="button" disabled={busy} onClick={() => choose('merge')}>
            Merge them
          </button>
        </div>
        <p className="adopt-note">
          Asked once for this week, on each phone. Nothing has been changed yet.
        </p>
      </div>
    </div>
  );
}

/**
 * A nudge to switch sharing on, not a gate.
 *
 * The app is fully usable signed out — that is what makes a genuinely offline
 * first run possible — so this can be dismissed. The dismissal goes to
 * localStorage rather than component state because this renders inside a screen
 * that unmounts on every tab change, and a dismissal held in state would come
 * straight back.
 */
const DISMISS_KEY = 'eden-eats-sharing-nudge-dismissed';

function dismissed(): boolean {
  try {
    return globalThis.localStorage?.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function SharingBanner({ onOpenSettings }: { onOpenSettings: () => void }): React.JSX.Element | null {
  const { configured, signedIn } = useSharing();
  const [hidden, setHidden] = useState(dismissed);

  if (!configured || signedIn || hidden) return null;

  const dismiss = (): void => {
    setHidden(true);
    try {
      globalThis.localStorage?.setItem(DISMISS_KEY, '1');
    } catch {
      // Nothing to do — the banner simply reappears next launch.
    }
  };

  return (
    <div className="sharing-nudge">
      <span>Sharing is off. Sign in to see each other&rsquo;s lists.</span>
      <button type="button" onClick={onOpenSettings}>Turn on</button>
      <button type="button" aria-label="Dismiss" onClick={dismiss}>&times;</button>
    </div>
  );
}

/** The sign-in panel, and the live status once signed in. Lives in Settings. */
export function SharingPanel(): React.JSX.Element {
  const { configured, signedIn, status, signIn, signOut, syncNow } = useSharing();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!configured) {
    return (
      <section className="sharing">
        <h2>Sharing</h2>
        <p>This build has no account configured, so the app is running on this device only.</p>
      </section>
    );
  }

  if (signedIn) {
    return (
      <section className="sharing">
        <h2>Sharing</h2>
        <p aria-live="polite">
          {status.state === 'syncing' ? 'Syncing…'
            : status.state === 'ok' ? 'Up to date.'
            : status.state === 'error' ? `Not synced: ${status.message}`
            : 'On.'}
        </p>
        <button type="button" onClick={syncNow}>Sync now</button>
        <button type="button" onClick={() => { void signOut(); }}>Sign out</button>
      </section>
    );
  }

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void signIn(email.trim(), password)
      .then(setError)
      .finally(() => setBusy(false));
  };

  return (
    <section className="sharing">
      <h2>Sharing</h2>
      <p>Sign in once on this phone to share plans and lists.</p>
      <form onSubmit={submit}>
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          Family password
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
      {error ? <p role="alert" className="sharing-error">{error}</p> : null}
    </section>
  );
}
